// ============================================================================
//  api.js — fetch helpers + useApi hook (SWR via dataCache.js)
// ============================================================================
//
//  useApi() keeps the same surface — { data, error, loading, refresh } —
//  but is now backed by the dataCache.js SWR layer. Behaviour changes:
//
//   * First paint after a cold browser open is hydrated from localStorage
//     (no spinner if we've ever loaded this URL before).
//   * Path changes (e.g. day-switch on Truck Volume) keep the previous
//     data on screen while the new query fetches in the background,
//     instead of blanking to a spinner.
//   * refresh() busts the cache for this path and triggers a fresh fetch
//     in the background. Existing data stays on screen until fresh data
//     arrives — no flash to loader.
//
//  Loading is DERIVED (loading === !data && !error) — true only when we
//  have nothing yet to show. That's what makes pages stop blanking on
//  refresh/path-change.
// ============================================================================

import { useEffect, useState, useCallback } from 'react';
import { cachedFetch, bust } from './dataCache.js';

export async function fetchJson(path) {
  const res = await fetch(path, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(res.status + ' ' + res.statusText + ': ' + text);
  }
  return res.json();
}

export async function postJson(path, body) {
  const opts = { method: 'POST', credentials: 'include' };
  if (body !== undefined) {
    opts.headers = { 'Content-Type': 'application/json' };
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(path, opts);
  const text = await res.text();
  if (!res.ok) throw new Error(res.status + ' ' + res.statusText + ': ' + text);
  try { return JSON.parse(text); } catch { return { ok: true, raw: text }; }
}

// PUT + DELETE parallels of postJson — same error-shape, same JSON body
// handling. Extracted from Reference.jsx 2026-07-03 during the code-review
// pass so all HTTP helpers live in one place instead of being re-implemented
// per-component.
export async function putJson(path, body) {
  const res = await fetch(path, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const text = await res.text();
  if (!res.ok) throw new Error(res.status + ' ' + text);
  try { return JSON.parse(text); } catch { return { ok: true }; }
}

export async function delJson(path) {
  const res = await fetch(path, { method: 'DELETE', credentials: 'include' });
  if (!res.ok) throw new Error(res.status + ' ' + await res.text());
  return { ok: true };
}

export async function uploadFile(path, file, fieldName) {
  const form = new FormData();
  form.append(fieldName || 'file', file, file.name);
  const res = await fetch(path, { method: 'POST', body: form, credentials: 'include' });
  const text = await res.text();
  if (!res.ok) throw new Error(res.status + ' ' + res.statusText + ': ' + text);
  try { return JSON.parse(text); } catch { return { ok: true, raw: text }; }
}

export function useApi(path, intervalMs) {
  // Seed initial data from localStorage synchronously so the first paint
  // skips the spinner entirely if we've ever loaded this URL before.
  const [data, setData] = useState(() => {
    if (!path) return null;
    try {
      const raw = localStorage.getItem('dc:' + path);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.value !== undefined) return parsed.value;
      }
    } catch {}
    return null;
  });
  const [error, setError] = useState(null);
  const ms = intervalMs || 5 * 60 * 1000;

  // Loading is derived: true only when there's nothing yet to show. This
  // is what makes refresh + path-change non-blanking.
  const loading = data === null && error === null;

  const load = useCallback(() => {
    // Guard: a null / falsy path means "not ready yet, don't fetch."
    // Callers use this to defer a dependent fetch until its parameters
    // are available.
    if (!path) return () => {};
    let isCurrent = true;
    cachedFetch(path, {
      onUpdate: (fresh) => { if (isCurrent) { setData(fresh); setError(null); } }
    })
      .then((d) => { if (isCurrent) { setData(d); setError(null); } })
      .catch((e) => { if (isCurrent) setError(e.message); });
    return () => { isCurrent = false; };
  }, [path]);

  useEffect(() => {
    const cleanup = load();
    const id = setInterval(load, ms);
    return () => { if (cleanup) cleanup(); clearInterval(id); };
  }, [load, ms]);

  const refresh = useCallback(() => {
    bust(path);
    load();
  }, [path, load]);

  return { data, error, loading, refresh };
}
